import { useState, useEffect } from 'react';
import { GrowthBook } from '@growthbook/growthbook-react';
import { setLanderContent } from '../utils/analytics';

const GB_BASE_URL = process.env.REACT_APP_GB_API;
const GB_BASE_KEY = process.env.REACT_APP_GB_KEY;

interface GrowthbookFeatures {
  videoMappings: { [slug: string]: string };
  authVideo: string;
  magicLink: string;
  // Top-level `name` from the `create-ai-slut-content` flag (e.g. "default").
  // Forwarded to analytics as `lander-content` so every GTM event carries the
  // content-variant identifier. Optional — old payloads without `name` keep
  // the 'default' fallback set in analytics.ts.
  name?: string;
}

let growthbookInstance: GrowthBook | null = null;
let featuresPromise: Promise<GrowthbookFeatures> | null = null;
let cachedFeatures: GrowthbookFeatures | null = null;

const initGrowthbook = async (): Promise<GrowthbookFeatures> => {
  // Return cached features if already loaded
  if (cachedFeatures) {
    return cachedFeatures;
  }

  // Return existing promise if already loading
  if (featuresPromise) {
    return featuresPromise;
  }

  // Create new promise to load features
  featuresPromise = (async () => {
    if (!growthbookInstance) {
      growthbookInstance = new GrowthBook({
        apiHost: GB_BASE_URL,
        clientKey: GB_BASE_KEY,
        enableDevMode: process.env.NODE_ENV === 'development',
      });

      await growthbookInstance.loadFeatures();
    }

    // The flag is a flat slug→URL map with an optional top-level `name` field
    // (e.g. "default"). Split `name` off so videoMappings stays a pure slug map.
    const raw = growthbookInstance.getFeatureValue<Record<string, string>>(
      'create-ai-slut-content',
      {},
    );
    const { name, ...videoMappings } = raw;
    const authVideo = growthbookInstance.getFeatureValue('create-ai-slut-auth-content', '');
    const magicLink = growthbookInstance.getFeatureValue('create-ai-slut-magicLink', 'OFF');

    cachedFeatures = {
      videoMappings,
      authVideo,
      magicLink,
      name,
    };

    return cachedFeatures;
  })();

  return featuresPromise;
};

export const useGrowthbook = () => {
  const [features, setFeatures] = useState<GrowthbookFeatures>({
    videoMappings: {},
    authVideo: '',
    magicLink: 'OFF',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const loadedFeatures = await initGrowthbook();
        setFeatures(loadedFeatures);
        // Push the resolved `name` into the GTM dataLayer so every subsequent
        // event the container fires carries `lander-content: <name>`.
        setLanderContent(loadedFeatures.name);
      } catch (error) {
        console.error('Failed to load Growthbook features:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeatures();
  }, []);

  return { features, loading };
};
